/**
 * News Article Page
 *
 * Individual article page with markdown rendering, breadcrumbs, CTA buttons,
 * and related news section
 */

import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Button,
  Breadcrumbs,
  Anchor,
  Paper,
  Divider,
  Box,
} from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Layout } from '../../shared/components';
import { useSEO } from '../../shared/hooks';
import { NewsCategoryBadge, NewsCard } from './components/News';
import { getNewsBySlug, NEWS_ITEMS } from '../../data/news/newsData';
import { trackNewsView } from '../../utils/analytics/newsTracking';
import { useAuth } from '../auth';
import type { CTAButton } from '../../data/news/newsTypes';

/**
 * Get icon component from icon name
 * Currently unused - will be needed for dynamic icon loading in the future
 */
// const _getIconComponent = (_iconName?: string) => {
//   // This would require dynamic imports for all Tabler icons
//   // For now, return null and we'll handle icons in CTA buttons separately
//   return null;
// };

/**
 * News Article Page Component
 *
 * Displays a single news article with full content, CTAs, and related news
 */
export function NewsArticle() {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();

  // Get article by slug
  const article = useMemo(() => {
    if (!slug) return undefined;
    return getNewsBySlug(slug);
  }, [slug]);

  // Get related news (same category, exclude current article, max 3)
  const relatedNews = useMemo(() => {
    if (!article) return [];

    return NEWS_ITEMS.filter(
      (item) => item.category === article.category && item.id !== article.id
    ).slice(0, 3);
  }, [article]);

  // Set page title
  useSEO({
    title: article?.title || 'Aktualność',
    description: article?.excerpt,
  });

  // Track article view
  useEffect(() => {
    if (article) {
      trackNewsView({
        news_id: article.id,
        news_category: article.category,
        news_title: article.title,
        is_authenticated: !!user,
      });
    }
  }, [article, user]);

  // 404 if article not found
  if (!slug || !article) {
    return (
      <Layout isAuthenticated={false}>
        <Container size="md" py={80}>
          <Stack gap="md" align="center">
            <Title order={1}>Nie znaleziono artykułu</Title>
            <Text c="dimmed">Artykuł o podanym adresie nie istnieje.</Text>
            <Button component={Link} to="/aktualnosci" leftSection={<IconArrowLeft size={16} />}>
              Powrót do aktualności
            </Button>
          </Stack>
        </Container>
      </Layout>
    );
  }

  // Format date
  const formattedDate = new Date(article.date).toLocaleDateString('pl-PL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Breadcrumbs items
  const breadcrumbItems = [
    { title: 'Strona główna', href: '/' },
    { title: 'Aktualności', href: '/aktualnosci' },
    { title: article.title, href: `/aktualnosci/${article.slug}` },
  ].map((item, index) => (
    <Anchor
      key={index}
      component={Link}
      to={item.href}
      c={index === 2 ? 'dimmed' : undefined}
    >
      {item.title}
    </Anchor>
  ));

  return (
    <Layout isAuthenticated={false}>
      <Container size="md" py={80}>
        <Stack gap="xl">
          {/* Breadcrumbs */}
          <Breadcrumbs separator="›">{breadcrumbItems}</Breadcrumbs>

          {/* Article Header */}
          <Stack gap="md">
            <Group gap="md">
              <NewsCategoryBadge category={article.category} size="lg" />
              {article.featured && (
                <Text size="sm" c="dimmed" fw={500}>
                  Wyróżnione
                </Text>
              )}
            </Group>

            <Title order={1} size="h2">
              {article.title}
            </Title>

            <Group gap="md">
              <Text size="sm" c="dimmed">
                {formattedDate}
              </Text>
              <Text size="sm" c="dimmed">
                •
              </Text>
              <Text size="sm" c="dimmed">
                {article.author}
              </Text>
              <Text size="sm" c="dimmed">
                •
              </Text>
              <Text size="sm" c="dimmed">
                {article.readTime} czytania
              </Text>
            </Group>
          </Stack>

          <Divider />

          {/* Article Content */}
          <Box
            style={{
              fontSize: '1.125rem',
              lineHeight: 1.7,
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Custom styling for markdown elements
                h1: ({ children }) => (
                  <Title order={1} size="h2" mb="lg" mt="xl">
                    {children}
                  </Title>
                ),
                h2: ({ children }) => (
                  <Title order={2} size="h3" mb="md" mt="xl">
                    {children}
                  </Title>
                ),
                h3: ({ children }) => (
                  <Title order={3} size="h4" mb="sm" mt="lg">
                    {children}
                  </Title>
                ),
                p: ({ children }) => (
                  <Text mb="md" style={{ lineHeight: 1.7 }}>
                    {children}
                  </Text>
                ),
                a: ({ href, children }) => (
                  <Anchor component={Link} to={href || '#'} c="ecoGreen">
                    {children}
                  </Anchor>
                ),
                ul: ({ children }) => (
                  <ul style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>{children}</ul>
                ),
                ol: ({ children }) => (
                  <ol style={{ marginBottom: '1rem', paddingLeft: '1.5rem' }}>{children}</ol>
                ),
                li: ({ children }) => (
                  <li style={{ marginBottom: '0.5rem' }}>{children}</li>
                ),
                code: ({ children, className }) => {
                  const isBlock = className?.includes('language-');
                  return isBlock ? (
                    <Paper withBorder p="md" bg="gray.0" mb="md">
                      <code style={{ fontSize: '0.9rem', fontFamily: 'monospace' }}>
                        {children}
                      </code>
                    </Paper>
                  ) : (
                    <code
                      style={{
                        backgroundColor: 'var(--mantine-color-gray-1)',
                        padding: '0.2rem 0.4rem',
                        borderRadius: '4px',
                        fontSize: '0.9em',
                        fontFamily: 'monospace',
                      }}
                    >
                      {children}
                    </code>
                  );
                },
                blockquote: ({ children }) => (
                  <Paper
                    withBorder
                    p="md"
                    mb="md"
                    style={{
                      borderLeft: '4px solid var(--mantine-color-ecoGreen-5)',
                      backgroundColor: 'var(--mantine-color-gray-0)',
                    }}
                  >
                    {children}
                  </Paper>
                ),
                hr: () => <Divider my="xl" />,
              }}
            >
              {article.content}
            </ReactMarkdown>
          </Box>

          {/* CTA Buttons */}
          {article.ctaButtons && article.ctaButtons.length > 0 && (
            <>
              <Divider />
              <Group gap="md" justify="center">
                {article.ctaButtons.map((cta: CTAButton, index: number) => (
                  <Button
                    key={index}
                    component={Link}
                    to={cta.url}
                    variant={cta.variant}
                    color={cta.color}
                    size="lg"
                  >
                    {cta.text}
                  </Button>
                ))}
              </Group>
            </>
          )}

          {/* Social Share Buttons - Placeholder for Agent 2 */}
          <Divider />
          <Paper withBorder p="md" radius="md" bg="gray.0">
            <Text size="sm" c="dimmed" ta="center">
              Udostępnij artykuł: [Przyciski social media - Agent 2]
            </Text>
          </Paper>

          {/* Related News Section */}
          {relatedNews.length > 0 && (
            <>
              <Divider mt="xl" />
              <Stack gap="md">
                <Title order={2} size="h3">
                  Powiązane aktualności
                </Title>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '1rem',
                  }}
                >
                  {relatedNews.map((item) => (
                    <NewsCard key={item.id} item={item} showCTA />
                  ))}
                </div>
              </Stack>
            </>
          )}

          {/* Back to News Button */}
          <Divider />
          <Button
            component={Link}
            to="/aktualnosci"
            variant="light"
            color="ecoGreen"
            leftSection={<IconArrowLeft size={16} />}
            size="md"
          >
            Powrót do aktualności
          </Button>
        </Stack>
      </Container>
    </Layout>
  );
}
